import { useAreGroupFiltersReady } from 'hooks/projectMap/useAreGroupFiltersReady';
import { useCurrentRegion } from 'state/currentRegion';
import { useGroupIdQuery } from 'state/queryParams';

/**
 * Whether the map's filter state is settled enough to fetch against.
 *
 * The default (no saved search) map filters aren't final at mount: the
 * region/state/city selection is empty until `useAvailableCities` resolves
 * and `useMapLocationFromUrl` sets the default region. Fetching before then
 * goes out with an empty region and is immediately superseded once the
 * region lands - the wasteful double request. So:
 *
 *  - No saved search selected: wait until the default region has resolved
 *    (`currentRegion` is set).
 *  - A saved search is selected: wait until that search's filters have been
 *    applied (`useAreGroupFiltersReady`), regardless of region - scoutbot
 *    searches are state-wide and carry no single region.
 */
export const useAreMapFiltersReady = (): boolean => {
  const { groupIdQuery } = useGroupIdQuery();
  const groupFiltersReady = useAreGroupFiltersReady();
  const { currentRegion } = useCurrentRegion();

  if (groupIdQuery) {
    return groupFiltersReady;
  }

  return !!currentRegion;
};
